import { useCallback, useEffect, useRef, useState } from 'react'

import { createStateFlags } from './async-state.helper'
import type { AsyncState, AsyncStateOptions, AsyncStateResult } from './async-state.interface'
import { useAsyncExecute } from './use-async-execute.hook'

export type { AsyncState, AsyncStateOptions, AsyncStateResult } from './async-state.interface'

export const useAsyncState = <T = void>(
  asyncFn: () => Promise<T>,
  options: AsyncStateOptions = {}
): AsyncStateResult<T> => {
  const { resetKey, autoExecute = false } = options

  const [state, setState] = useState<AsyncState>('idle')
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<Error | null>(null)

  const previousResetKeyRef = useRef<string | number | null | undefined>(resetKey)
  const isMountedRef = useRef(true)
  const isPendingRef = useRef(false)
  const asyncFnRef = useRef(asyncFn)
  asyncFnRef.current = asyncFn

  useEffect(() => {
    if (previousResetKeyRef.current !== resetKey) {
      setState('idle')
      setData(null)
      setError(null)
      isPendingRef.current = false
      previousResetKeyRef.current = resetKey
    }
  }, [resetKey])

  useEffect(
    () => () => {
      isMountedRef.current = false
    },
    []
  )

  const execute = useAsyncExecute({
    asyncFnRef,
    isPendingRef,
    isMountedRef,
    setState,
    setData,
    setError,
  })

  const reset = useCallback(() => {
    setState('idle')
    setData(null)
    setError(null)
    isPendingRef.current = false
  }, [])

  useEffect(() => {
    if (autoExecute && state === 'idle') {
      execute()
    }
  }, [autoExecute, state, execute])

  return { state, data, error, ...createStateFlags(state), execute, reset }
}
